import React, { useEffect, Fragment, useState } from "react";
import Breadcrumb from "../../../components/common/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";
import {
  DeleteFinancialYear,
  GetFinancialYears
} from "../../../api/utility/financialYearAPI";
import { formatDate } from "../../../utils/dateFormatter";

const ListFinancialYear = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchFinancialYears = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetFinancialYears();

      if (response.success) {
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,
          name: item.name || "N/A",
          startDate: formatDate(item.startDate),
          endDate: formatDate(item.endDate),
          status: item.isActive ? "Active" : "Inactive",
          createdAt: formatDate(item.createdAt)
        }));

        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message || "Something went wrong");
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchFinancialYears();
  }, []);

  useEffect(
    () => {
      const results = data.filter(item =>
        Object.values(item)
          .join(" ")
          .toLowerCase()
          .includes(search.toLowerCase())
      );
      setFilteredData(results);
    },
    [search, data]
  );

  const handleEditClick = id => {
    navigate(`/utility/financial-year/edit/${id}`);
  };

  const handleDeleteClick = async id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will perform a soft delete on this Financial Year!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeleteFinancialYear(id);
          if (response.success) {
            showToast("success", response.message);
            const updatedData = data.filter(item => item.id !== id);
            setData(updatedData);
            setFilteredData(updatedData);
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  const columns = [
    { name: "S.No", selector: row => row.index, width: "10%", sortable: true },
    { name: "Year Name", selector: row => row.name, sortable: true },
    { name: "Start Date", selector: row => row.startDate, sortable: true },
    { name: "End Date", selector: row => row.endDate, sortable: true },
    {
      name: "Status",
      selector: row => row.status,
      sortable: true,
      cell: row =>
        <span
          className={`badge ${row.status === "Active"
            ? "bg-success"
            : "bg-danger"}`}
        >
          {row.status}
        </span>
    },
    {
      name: "Actions",
      cell: row =>
        <div>
          <button
            className="btn btn-primary btn-sm me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil" />
          </button>
          <UncontrolledTooltip target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger btn-sm"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash" />
          </button>
          <UncontrolledTooltip target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true
    }
  ];

  const headers = [
    { label: "Year Name", key: "name" },
    { label: "Start Date", key: "startDate" },
    { label: "End Date", key: "endDate" },
    { label: "Status", key: "status" },
    { label: "Created At", key: "createdAt" }
  ];

  const ExportCSV = ({ data }) =>
    <CSVLink data={data} headers={headers} filename="financial_years.csv">
      <button className="btn btn-primary" id="export">
        <i className="fa fa-download me-2" />Export CSV
      </button>
      <UncontrolledTooltip target="export">Export to CSV</UncontrolledTooltip>
    </CSVLink>;

  return (
    <Fragment>
      <Breadcrumb parent="Masters" title="Financial Years" />
      <div className="container-fluid">
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center">
              <h5>Financial Year List</h5>
              <Link to={`/utility/financial-year/add`}>
                <button className="btn btn-primary" id="create">
                  <i className="fa fa-plus me-2" />Add New Year
                </button>
                <UncontrolledTooltip target="create">
                  Add Financial Year
                </UncontrolledTooltip>
              </Link>
            </div>

            <div className="row mt-3">
              <div className="col-sm-4">
                <input
                  className="form-control"
                  placeholder="Search years..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className="col-sm-8 text-end">
                <ExportCSV data={data} />
              </div>
            </div>
          </div>

          <div className="card-body">
            <div className="table-responsive">
              <DataTable
                columns={columns}
                data={filteredData}
                pagination
                highlightOnHover
                striped
                responsive
              />
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListFinancialYear;
